import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Youtube, Facebook, Instagram, Twitch, Eye, Users, TrendingUp, Mail, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import MediaKitNav from "@/components/MediaKitNav";
import AnimatedCounter from "@/components/AnimatedCounter";

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MediaKitPage() {
  const [stats, setStats] = useState(null);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragStartPos, setDragStartPos] = useState(0);
  const carouselRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  // Smooth auto-scroll with requestAnimationFrame
  useEffect(() => {
    if (featuredVideos.length > 1 && carouselRef.current) {
      const carousel = carouselRef.current;
      const speed = 0.2; // pixels per frame - slower for smoother feel
      
      const animate = () => {
        if (!isPaused && !isDragging && carousel) {
          setScrollPosition(prev => {
            const maxScroll = carousel.scrollWidth / 2;
            let newPos = prev + speed;
            if (newPos >= maxScroll) {
              newPos = 0;
            }
            return newPos;
          });
        }
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [featuredVideos, isPaused, isDragging]);

  const fetchData = async () => {
    try {
      const [statsRes, videosRes, logosRes] = await Promise.all([
        axios.get(`${API}/media-kit/stats`),
        axios.get(`${API}/media-kit/featured-videos`).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/media-kit/client-logos`).catch(() => ({ data: { logos: [] } }))
      ]);
      setStats(statsRes.data);
      setFeaturedVideos(videosRes.data.videos || []);
      setClientLogos(logosRes.data.logos || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Carousel scroll functions
  const scrollCarousel = (direction) => {
    const scrollAmount = 420;
    setScrollPosition(prev => {
      const maxScroll = carouselRef.current ? carouselRef.current.scrollWidth / 2 : 0;
      let newPos = direction === 'left' ? prev - scrollAmount : prev + scrollAmount;
      if (newPos < 0) newPos = maxScroll + newPos;
      if (newPos >= maxScroll) newPos = newPos - maxScroll;
      return newPos;
    });
  };

  // Touch/Drag handlers for mobile
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setDragStartPos(scrollPosition);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;
    const maxScroll = carouselRef.current ? carouselRef.current.scrollWidth / 2 : 0;
    let newPos = dragStartPos + diff;
    if (newPos < 0) newPos = maxScroll + newPos;
    if (newPos >= maxScroll) newPos = newPos - maxScroll;
    setScrollPosition(newPos);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Formatear número de vistas
  const formatViews = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num?.toString() || "0";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img 
          src="https://customer-assets-0z36b82j.emergentagent.net/job_6374765b-0927-4fa7-ace6-9a95eb25e587/artifacts/6cbu7wu9_LOADING.gif" 
          alt="Cargando..."
          className="w-48 h-auto"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col text-white">
      <Navbar />
      
      {/* ===== HEADER SECTION - White with opacity ===== */}
      <section className="relative pt-44 pb-12">
        {/* White background with opacity - reveals BG pattern */}
        <div className="absolute inset-0 bg-white/40" />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 
              className="text-5xl md:text-7xl font-bold mb-4 text-black"
              style={{ 
                fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
                WebkitTextStroke: '8px white',
                paintOrder: 'stroke fill'
              }}
            >
              MEDIA KIT
            </h1>
            <p 
              className="text-lg md:text-xl text-[#FFE500]"
              style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
            >
              Vete a la Versh · Darkar Company Studios
            </p>
          </div>

          {/* Navigation */}
          <MediaKitNav />
        </div>
      </section>

      {/* ===== STATS SECTION - Black with opacity ===== */}
      <section className="relative py-16">
        {/* Black background with opacity - reveals BG pattern */}
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          {/* Ecosistema Digital Title */}
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-white"
            style={{ 
              fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
              WebkitTextStroke: '4px black',
              paintOrder: 'stroke fill'
            }}
          >
            ECOSISTEMA DIGITAL
          </h2>

          {/* Main Stats - Big Numbers */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-12">
            {/* YouTube */}
            <div className="text-center" data-testid="stat-youtube">
              <Youtube className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-red-500" />
              <div 
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.youtube?.subscribers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Suscriptores</p>
            </div>

            {/* Facebook */}
            <div className="text-center" data-testid="stat-facebook">
              <Facebook className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-blue-500" />
              <div 
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.facebook?.followers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>

            {/* Twitch */}
            <div className="text-center" data-testid="stat-twitch">
              <Twitch className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-purple-500" />
              <div 
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.twitch?.followers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>

            {/* Instagram */}
            <div className="text-center" data-testid="stat-instagram">
              <Instagram className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-pink-500" />
              <div 
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.instagram?.followers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>

            {/* TikTok - col-span-2 en móvil para centrarlo */}
            <div className="text-center col-span-2 md:col-span-1" data-testid="stat-tiktok">
              <TikTokIcon className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-white" />
              <div 
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.tiktok?.followers || 708000} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>
          </div>

          {/* Total Reach - Below social logos */}
          <div className="text-center mt-8 mb-12">
            <div className="inline-block" data-testid="stat-total-reach">
              <div 
                className="text-4xl md:text-5xl lg:text-6xl font-black text-[#FFE500]"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter 
                  value={
                    (stats?.youtube?.subscribers || 0) + 
                    (stats?.facebook?.followers || 0) + 
                    (stats?.twitch?.followers || 0) + 
                    (stats?.instagram?.followers || 0) +
                    (stats?.tiktok?.followers || 708000)
                  } 
                  duration={3000} 
                />
              </div>
              <p className="text-gray-300 text-lg md:text-xl uppercase tracking-wide">Alcance Total</p>
            </div>
          </div>

          {/* Total Views Highlight */}
          <div className="text-center pt-8 border-t border-white/20">
            <div className="inline-block" data-testid="stat-total-views">
              <div 
                className="text-5xl md:text-6xl lg:text-7xl font-black text-[#FFE500]"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                +<AnimatedCounter value={stats?.youtube?.total_views || 0} duration={3000} format="full" />
              </div>
              <p className="text-gray-300 text-lg md:text-xl uppercase tracking-wide">Reproducciones Totales</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== NUESTROS CLIENTES SECTION - White with opacity ===== */}
      {(clientLogos.length > 0 || featuredVideos.length > 0) && (
        <section className="relative py-16">
          {/* White background with opacity - reveals BG pattern */}
          <div className="absolute inset-0 bg-white/40" />
          
          <div className="relative z-10">
            <h2 
              className="text-3xl md:text-4xl font-bold text-center mb-12 text-black px-4"
              style={{ 
                fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
                WebkitTextStroke: '6px white',
                paintOrder: 'stroke fill'
              }}
            >
              NUESTROS CLIENTES
            </h2>

            {/* Client Logos Grid */}
            {clientLogos.length > 0 && (
              <div className="mb-12 px-4 md:px-8 lg:px-12">
                <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 lg:gap-8 xl:flex-nowrap xl:justify-between">
                  {clientLogos.map((logo, index) => (
                    <div
                      key={logo.id || index}
                      data-testid={`client-logo-${index}`}
                      className="flex-shrink-0"
                    >
                      <img
                        src={logo.logo_url}
                        alt={logo.name}
                        className="h-8 md:h-10 lg:h-12 xl:h-14 w-auto object-contain"
                        title={logo.name}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Videos Carousel - FULL WIDTH */}
            {featuredVideos.length > 0 && (
              <div 
                className="relative overflow-hidden w-full touch-pan-y"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Carousel Navigation */}
                {featuredVideos.length > 1 && (
                  <>
                    <button
                      onClick={() => scrollCarousel('left')}
                      className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 bg-[#FFE500] hover:bg-[#FFE500]/90 text-black p-2 md:p-3 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110"
                      data-testid="carousel-prev"
                    >
                      <ChevronLeft className="w-5 h-5 md:w-8 md:h-8" strokeWidth={3} />
                    </button>
                    <button
                      onClick={() => scrollCarousel('right')}
                      className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 bg-[#FFE500] hover:bg-[#FFE500]/90 text-black p-2 md:p-3 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110"
                      data-testid="carousel-next"
                    >
                      <ChevronRight className="w-5 h-5 md:w-8 md:h-8" strokeWidth={3} />
                    </button>
                  </>
                )}

                {/* Carousel Track */}
                <div
                  ref={carouselRef}
                  className="flex gap-4 pb-4 transition-transform duration-300 ease-out"
                  style={{ 
                    transform: `translateX(-${scrollPosition}px)`,
                    width: 'max-content'
                  }}
                >
                  {/* Original + Duplicated for seamless loop */}
                  {[...featuredVideos, ...(featuredVideos.length > 1 ? featuredVideos : [])].map((video, index) => {
                    // Extract YouTube video ID
                    const getYouTubeId = (url) => {
                      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                      const match = url.match(regExp);
                      return (match && match[2].length === 11) ? match[2] : null;
                    };
                    
                    // Extract Vimeo video ID
                    const getVimeoId = (url) => {
                      const regExp = /vimeo\.com\/(\d+)/;
                      const match = url.match(regExp);
                      return match ? match[1] : null;
                    };

                    const youtubeId = getYouTubeId(video.video_url);
                    const vimeoId = getVimeoId(video.video_url);

                    return (
                      <div 
                        key={`${video.id || index}-${index}`}
                        className="flex-shrink-0 w-[85vw] md:w-[400px] bg-black rounded-xl overflow-hidden shadow-lg"
                        data-testid={`featured-video-${index}`}
                      >
                        {/* Embedded Video */}
                        <div className="relative aspect-video bg-black">
                          {youtubeId ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${youtubeId}`}
                              title={video.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            />
                          ) : vimeoId ? (
                            <iframe
                              src={`https://player.vimeo.com/video/${vimeoId}`}
                              title={video.title}
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#E91E8C]/20 to-[#FFE500]/20">
                              <Play className="w-16 h-16 text-white/50" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="p-4">
                          <h3 className="font-bold text-white mb-2 text-lg">
                            {video.title}
                          </h3>
                          {video.description && (
                            <p className="text-gray-400 text-sm mb-3">{video.description}</p>
                          )}
                          <div className="flex items-center justify-between">
                            {/* Platform badge */}
                            <div className="flex items-center gap-2">
                              {video.platform === 'youtube' && (
                                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
                                  <Youtube className="w-3 h-3" /> YouTube
                                </span>
                              )}
                              {video.platform === 'vimeo' && (
                                <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                  Vimeo
                                </span>
                              )}
                            </div>
                            {/* Views badge */}
                            {video.views > 0 && (
                              <div className="flex items-center gap-1 text-gray-400 text-sm">
                                <Eye className="w-4 h-4" />
                                {formatViews(video.views)} vistas
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== DEMOGRAPHICS SECTION - Black with opacity ===== */}
      <section className="relative py-16">
        {/* Black background with opacity - reveals BG pattern */}
        <div className="absolute inset-0 bg-black/70" />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
          >
            <span className="text-[#FFE500]">NUESTRA</span>{" "}
            <span className="text-white">AUDIENCIA</span>
          </h2>

          {/* Stats Row - Similar to social networks */}
          <div className="grid grid-cols-3 gap-6 md:gap-8 mb-12 max-w-3xl mx-auto">
            {/* Men */}
            <div className="text-center" data-testid="demo-men">
              <Users className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-blue-400" />
              <div 
                className="text-4xl md:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                {stats?.demographics?.gender?.male || 67}%
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Hombres</p>
            </div>

            {/* Women */}
            <div className="text-center" data-testid="demo-women">
              <Users className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-pink-400" />
              <div 
                className="text-4xl md:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                {stats?.demographics?.gender?.female || 33}%
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Mujeres</p>
            </div>

            {/* Age Range */}
            <div className="text-center" data-testid="demo-age">
              <TrendingUp className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-[#FFE500]" />
              <div 
                className="text-4xl md:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                15 - 34
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Edad Principal</p>
            </div>
          </div>

          {/* Countries */}
          <div className="text-center pt-8 border-t border-white/20" data-testid="demo-countries">
            <p 
              className="text-xl md:text-2xl font-bold text-white mb-6"
              style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
            >
              Países Principales
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {stats?.demographics?.countries && 
                Object.entries(stats.demographics.countries).slice(0, 5).map(([country], index) => (
                  <span 
                    key={country}
                    className={`px-4 py-2 rounded-full font-bold ${
                      index === 0 
                        ? "bg-[#FFE500] text-black" 
                        : "bg-white/10 text-white border border-white/20"
                    }`}
                  >
                    {country}
                  </span>
                ))
              }
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT SECTION - White with opacity ===== */}
      <section className="relative py-16">
        {/* White background with opacity - reveals BG pattern */}
        <div className="absolute inset-0 bg-white/40" />
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-8 text-black"
            style={{ 
              fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
              WebkitTextStroke: '6px white',
              paintOrder: 'stroke fill'
            }}
          >
            UNA HISTORIA QUE MARCÓ EL INTERNET
          </h2>
          
          <div className="space-y-6 text-lg text-black leading-relaxed">
            <p>
              <strong>Vete a la Versh</strong> no solo creció en internet, también lo hizo con toda una generación.
            </p>
            
            <p>
              Desde sus primeros episodios, <strong>Darkar Company</strong> convirtió humor, irreverencia y animación independiente en un fenómeno de la cultura digital latinoamericana.
            </p>
            
            <p>
              Hoy, con más de <span className="text-[#FFE500] font-bold" style={{ textShadow: '1px 1px 0 #000' }}>2 mil millones de reproducciones</span> y una comunidad de más de <span className="text-[#FFE500] font-bold" style={{ textShadow: '1px 1px 0 #000' }}>27 millones de seguidores</span>, personajes como <strong>Darkar</strong> y <strong>Mecoboy</strong> siguen conectando la nostalgia de quienes crecieron con la serie con una nueva y joven generación de fans.
            </p>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION - Black with opacity ===== */}
      <section className="relative py-20">
        {/* Black background with opacity - reveals BG pattern */}
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
          >
            <span className="text-[#FFE500]">SÉ PARTE</span>{" "}
            <span className="text-white">DE LO QUE SIGUE</span>
          </h2>
          
          <div className="space-y-4 mb-10">
            <p className="text-xl text-gray-300">
              Una comunidad construida durante años. Una nueva etapa por escribir.
            </p>
            <p className="text-xl text-white font-semibold">
              Creemos juntos la próxima historia de Darkar Company
            </p>
          </div>
          
          <Link to="/contacto">
            <Button 
              className="bg-white hover:bg-gray-100 text-black text-lg px-10 py-6 font-black hand-drawn-subtle border-[3px] border-black"
              style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              data-testid="cta-contact"
            >
              <Mail className="mr-2" />
              HABLEMOS
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
